/**
 * 系统管理 Dashboard — 入口模块
 * 聚合所有子模块，处理初始化顺序和事件委托
 */

import { authFetch } from './api.js';
import { initSidebarNav, checkUserRole, drilldownGoBack, switchView } from './navigation.js';
import { initListings, restoreListing, deleteListing } from './listings.js';
import { initUsers, toggleUserStatus, changeUserRole, assignGroup, bindErpUser, updateUserApproval, resetUserPassword, deleteUser } from './users.js?v=20260805-user-delete-reset-pwd';
import { initGroups, manageGroup, deleteGroup } from './groups.js';
import { initStats } from './stats.js';
import { initJobs } from './jobs.js';
import { initDrilldown } from './drilldown.js';
import { initAdminUsage } from './admin-usage.js';
import { initMemberConsumption } from './member-consumption.js';
import { initFeedback, markFeedbackFixed, reopenFeedback, showFeedbackDetail } from './feedback.js';
import { initErrors, showSysErrDetail } from './errors.js';
import { initProductResearchLogs, showProductResearchLogDetail } from './product-research-logs.js';
import { initProductResearchConfig } from './product-research-config.js';
import { initProductResearchAlert } from './product-research-alert.js';
import { initAllListings } from './all-listings.js';
import { initImageManager } from './image-manager.js';
import { initVideoManager } from './video-manager.js';
import { initSystemHealth } from './system-health.js';
import { initCommandCenter } from './command-center.js';
import { initFeishuSyncCenter } from './feishu-sync-center.js';
import { initProviderSettings } from './provider-settings.js';
import { initSifLogin } from './sif-login.js';
import { initModuleManagement } from './module-management.js';
import { initAdRuleManagement } from './ad-rule-management.js';
import { initAichatMonitor } from './aichat-monitor.js';
import { initAgentOps } from './agent.js';
import { initCostCalculationSettings } from './cost-calculation-settings.js';
import { initIdentityViewFragment } from './identity-view-fragment.js';
import { initGroupsViewFragment } from './groups-view-fragment.js';
import { initModulesViewFragment } from './modules-view-fragment.js';
import { initJobsViewFragment } from './jobs-view-fragment.js';
import { initProductResearchConfigViewFragment } from './product-research-config-view-fragment.js';
import { initAdRulesViewFragment } from './ad-rules-view-fragment.js';
import { initProductResearchLogsViewFragment } from './product-research-logs-view-fragment.js';
import { initSystemErrorsViewFragment } from './system-errors-view-fragment.js';
import { initProductResearchAlertViewFragment } from './product-research-alert-view-fragment.js';
import { initSystemHealthViewFragment } from './system-health-view-fragment.js';
import { initImageManagerViewFragment } from './image-manager-view-fragment.js';
import { initAiRolesViewFragment } from './ai-roles-view-fragment.js';
import { initProviderSettingsViewFragment } from './provider-settings-view-fragment.js';
import { initSifLoginViewFragment } from './sif-login-view-fragment.js';
import { initCostCalculationViewFragment } from './cost-calculation-view-fragment.js';
import { initCommandCenterViewFragment } from './command-center-view-fragment.js';
import { initFeishuSyncCenterViewFragment } from './feishu-sync-center-view-fragment.js';
import { initVideoManagerViewFragment } from './video-manager-view-fragment.js';
import { initAgentOpsViewFragment } from './agent-ops-view-fragment.js';
import { renderAppVersionBadge } from '../app-version-badge.js';
import './ai-roles.js';

// ==================== 事件委托（替代内联 onclick） ====================
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-action]');
  if (!btn) return;

  const action = btn.dataset.action;
  const id = btn.dataset.id;
  const arg1 = btn.dataset.arg1;
  const arg2 = btn.dataset.arg2;
  const arg3 = btn.dataset.arg3;

  const actions = {
    'restore-listing': () => restoreListing(id),
    'delete-listing': () => deleteListing(id),
    'toggle-user': () => toggleUserStatus(id, arg1),
    'edit-user': () => changeUserRole(id, '', arg1),
    'approve-user': () => updateUserApproval(id, 'approved'),
    'reject-user': () => updateUserApproval(id, 'rejected'),
    'change-role': () => changeUserRole(id, arg1, arg2),
    'assign-group': () => assignGroup(id, arg1),
    'bind-erp-user': () => bindErpUser(id, arg1),
    'reset-password-user': () => resetUserPassword(id, arg1),
    'delete-user': () => deleteUser(id, arg1),
    'manage-group': () => manageGroup(id),
    'delete-group': () => deleteGroup(id),
    'mark-feedback-fixed': () => markFeedbackFixed(id),
    'show-feedback-detail': () => showFeedbackDetail(btn.dataset.id),
    'reopen-feedback': () => reopenFeedback(id),
    'show-syserr-detail': () => showSysErrDetail(id),
    'show-product-research-log-detail': () => showProductResearchLogDetail(id),
    'drilldown-back': () => drilldownGoBack(),
    'page-change': () => {
      const fn = window[btn.dataset.callback];
      if (typeof fn === 'function') fn(parseInt(btn.dataset.page, 10));
    },
  };

  if (actions[action]) {
    e.preventDefault();
    actions[action]();
  }
});

// ==================== 详情弹窗 ====================
function showDetailModal(title, content) {
  document.getElementById('detail-modal-title').textContent = title;
  let displayText = content;
  try {
    displayText = JSON.parse(content);
    if (typeof displayText === 'object') displayText = JSON.stringify(displayText, null, 2);
  } catch { /* not JSON */ }
  document.getElementById('detail-modal-content').textContent = typeof displayText === 'string' ? displayText : JSON.stringify(displayText, null, 2);
  document.getElementById('detail-modal').hidden = false;
}

function closeDetailModal() {
  document.getElementById('detail-modal').hidden = true;
}

function initSystemDateControls() {
  const dateInputs = document.querySelectorAll('input[type="date"]');
  if (!dateInputs.length) return;

  const openPicker = (input) => {
    if (!input || input.disabled || input.readOnly) return;
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch (error) {
        // Safari and older browsers may not support showPicker in this context
      }
    }
    input.focus({ preventScroll: true });
  };

  dateInputs.forEach((input) => {
    input.addEventListener('click', () => {
      openPicker(input);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openPicker(input);
    });
  });
}

// ==================== 初始化 ====================
document.addEventListener('DOMContentLoaded', async () => {
  // H2: No localStorage token — verify session via /api/auth/me
  try {
    const resp = await authFetch('/api/auth/me');
    if (!resp.ok) { window.location.href = '/login.html'; return; }
  } catch {
    window.location.href = '/login.html';
    return;
  }
  renderAppVersionBadge();

  // Register all view loaders BEFORE any navigation
  initListings();
  initUsers();
  initGroups();
  initStats();
  initJobs();
  initDrilldown();
  initAdminUsage();
  initMemberConsumption();
  initFeedback();
  initErrors();
  initProductResearchLogs();
  initProductResearchConfig();
  initProductResearchAlert();
  initAllListings();
  initAichatMonitor();
  initAgentOps();
  initImageManager();
  initVideoManager();
  initSystemHealth();
  initCommandCenter();
  initFeishuSyncCenter();
  initProviderSettings();
  initSifLogin();
  initCostCalculationSettings();
  initAdRuleManagement();
  initModuleManagement();
  initIdentityViewFragment({ onMounted: initUsers });
  initGroupsViewFragment({ onMounted: initGroups });
  initModulesViewFragment({ onMounted: initModuleManagement });
  initJobsViewFragment({ onMounted: initJobs });
  initProductResearchConfigViewFragment({ onMounted: initProductResearchConfig });
  initAdRulesViewFragment({ onMounted: initAdRuleManagement });
  initProductResearchLogsViewFragment({ onMounted: initProductResearchLogs });
  initSystemErrorsViewFragment({ onMounted: initErrors });
  initProductResearchAlertViewFragment({ onMounted: initProductResearchAlert });
  initSystemHealthViewFragment({ onMounted: initSystemHealth });
  initImageManagerViewFragment({ onMounted: initImageManager });
  initAiRolesViewFragment();
  initProviderSettingsViewFragment({ onMounted: initProviderSettings });
  initSifLoginViewFragment({ onMounted: initSifLogin });
  initCostCalculationViewFragment({ onMounted: initCostCalculationSettings });
  initCommandCenterViewFragment({ onMounted: initCommandCenter });
  initFeishuSyncCenterViewFragment({ onMounted: initFeishuSyncCenter });
  initVideoManagerViewFragment({ onMounted: initVideoManager });
  initAgentOpsViewFragment({ onMounted: initAgentOps });

  // Init sidebar navigation
  initSidebarNav();

  // 全站系统管理日期控件：保证点击输入框区域可直接打开日历（含显示日期区域）
  initSystemDateControls();

  // Check user role (updates sidebar, shows admin nav)
  await checkUserRole(authFetch);

  // Bind remaining static events
  bindStaticEvents();

  // Check URL params for direct view navigation
  const urlParams = new URLSearchParams(window.location.search);
  const urlView = urlParams.get('view');
  if (urlView) {
    switchView(urlView);
  } else {
    // Load default view
    switchView('listings');
  }
});

// ==================== 静态事件绑定 ====================
function bindStaticEvents() {
  const bindings = [
    ['detail-modal-close', 'click', closeDetailModal],
    ['image-gallery-close', 'click', () => { document.getElementById('image-gallery-modal').hidden = true; }],
  ];

  bindings.forEach(([id, event, handler]) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener(event, handler);
  });

  // 返回按钮（多个）
  ['users-back-btn', 'user-approvals-back-btn', 'jobs-back-btn', 'usage-back-btn', 'feedback-back-btn', 'syserr-back-btn', 'drilldown-back-btn'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('click', drilldownGoBack);
  });

  // 模态框背景点击关闭
  const modal = document.getElementById('detail-modal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeDetailModal();
    });
  }

  // Modal overlay click-to-close (generic)
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      e.target.hidden = true;
    }
  });
}
